import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Save, Wand2, Copy, CheckCircle2, Loader2, X 
} from 'lucide-react';

const employmentTypes = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' },
  { value: 'freelance', label: 'Freelance' }
];

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

const defaultStages = [
  { id: 'new', label: 'New Application', required: true },
  { id: 'HR_screen', label: 'HR Screening' },
  { id: 'tech_round_1', label: 'Technical Round 1' },
  { id: 'tech_round_2', label: 'Technical Round 2' },
  { id: 'client_round', label: 'Client Round' },
  { id: 'HR_round', label: 'HR Round' },
  { id: 'offered', label: 'Offered', required: true }
];

export default function NewRequisitionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatingJD, setGeneratingJD] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [prefSkillInput, setPrefSkillInput] = useState('');
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    hiring_manager_id: '',
    location: '',
    employment_type: 'full_time',
    experience_min: 0,
    experience_max: 5,
    skills_required: [],
    skills_preferred: [],
    job_description: '',
    budget_min: '',
    budget_max: '',
    priority: 'medium',
    number_of_positions: 1,
    target_closure_date: '',
    enabled_stages: ['new', 'HR_screen', 'tech_round_1', 'tech_round_2', 'client_round', 'HR_round', 'offered']
  });

  const [generatedJD, setGeneratedJD] = useState({
    full_jd: '',
    social_snippet: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = (type) => {
    const input = type === 'required' ? skillInput : prefSkillInput;
    const field = type === 'required' ? 'skills_required' : 'skills_preferred';
    
    if (input.trim() && !formData[field].includes(input.trim())) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], input.trim()]
      }));
      type === 'required' ? setSkillInput('') : setPrefSkillInput('');
    }
  };

  const removeSkill = (type, skill) => {
    const field = type === 'required' ? 'skills_required' : 'skills_preferred';
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(s => s !== skill)
    }));
  };

  const toggleStage = (stageId) => {
    const stage = defaultStages.find(s => s.id === stageId);
    if (stage?.required) return;
    
    setFormData(prev => ({
      ...prev,
      enabled_stages: prev.enabled_stages.includes(stageId)
        ? prev.enabled_stages.filter(s => s !== stageId)
        : [...prev.enabled_stages, stageId]
    }));
  };

  const generateJD = async () => {
    setGeneratingJD(true);
    try {
      const response = await api.post('/recruitment/ai/generate-jd', {
        title: formData.title,
        department: formData.department,
        location: formData.location,
        experience_min: formData.experience_min,
        experience_max: formData.experience_max,
        skills_required: formData.skills_required,
        skills_preferred: formData.skills_preferred,
        employment_type: formData.employment_type,
        role_summary: formData.job_description,
        keywords: []
      });
      setGeneratedJD({
        full_jd: response.data.full_jd,
        social_snippet: response.data.social_snippet
      });
    } catch (error) {
      console.error('Error generating JD:', error);
      alert('Failed to generate JD. Please try again.');
    } finally {
      setGeneratingJD(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        experience_min: parseInt(formData.experience_min),
        experience_max: parseInt(formData.experience_max),
        number_of_positions: parseInt(formData.number_of_positions)
      };
      
      const response = await api.post('/recruitment/requisitions', payload);
      navigate(`/recruitment/requisitions/${response.data.id}`);
    } catch (error) {
      console.error('Error creating requisition:', error);
      alert('Failed to create requisition. Please check all fields and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/recruitment/requisitions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">New Job Requisition</h1>
          <p className="text-gray-500">Create a new hiring position</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="title">Job Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., Senior Full Stack Developer"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="department">Department *</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      placeholder="e.g., Engineering"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="e.g., Bangalore, India (Remote-friendly)"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="hiring_manager">Hiring Manager *</Label>
                    <Select
                      value={formData.hiring_manager_id}
                      onValueChange={(value) => handleInputChange('hiring_manager_id', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hiring manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.user_id || emp.id}>
                            {emp.name} - {emp.designation}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="employment_type">Employment Type</Label>
                    <Select
                      value={formData.employment_type}
                      onValueChange={(value) => handleInputChange('employment_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {employmentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Experience Range (Years)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={formData.experience_min}
                        onChange={(e) => handleInputChange('experience_min', e.target.value)}
                        className="w-20"
                      />
                      <span>to</span>
                      <Input
                        type="number"
                        min="0"
                        value={formData.experience_max}
                        onChange={(e) => handleInputChange('experience_max', e.target.value)}
                        className="w-20"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Budget Range (LPA)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.budget_min}
                        onChange={(e) => handleInputChange('budget_min', e.target.value)}
                        placeholder="Min"
                        className="w-24"
                      />
                      <span>to</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.budget_max}
                        onChange={(e) => handleInputChange('budget_max', e.target.value)}
                        placeholder="Max"
                        className="w-24"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Skills</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Required Skills</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder="Type skill and press Enter"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill('required'))}
                    />
                    <Button type="button" onClick={() => addSkill('required')}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills_required.map((skill, idx) => (
                      <Badge key={idx} variant="default" className="flex items-center gap-1">
                        {skill}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill('required', skill)} />
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Preferred Skills (Nice to have)</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={prefSkillInput}
                      onChange={(e) => setPrefSkillInput(e.target.value)}
                      placeholder="Type skill and press Enter"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill('preferred'))}
                    />
                    <Button type="button" variant="outline" onClick={() => addSkill('preferred')}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills_preferred.map((skill, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1">
                        {skill}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill('preferred', skill)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
                <CardDescription>Write a brief description or use AI to generate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Brief Role Summary</Label>
                  <Textarea
                    value={formData.job_description}
                    onChange={(e) => handleInputChange('job_description', e.target.value)}
                    placeholder="Describe the role responsibilities and what the candidate will work on..."
                    rows={4}
                  />
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateJD}
                  disabled={generatingJD || !formData.title}
                  className="w-full"
                >
                  {generatingJD ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating with AI...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Generate LinkedIn JD with AI</>
                  )}
                </Button>

                {generatedJD.full_jd && (
                  <Tabs defaultValue="full" className="mt-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="full">Full JD</TabsTrigger>
                      <TabsTrigger value="snippet">Social Snippet</TabsTrigger>
                    </TabsList>
                    <TabsContent value="full">
                      <div className="relative">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(generatedJD.full_jd)}
                        >
                          {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Textarea
                          value={generatedJD.full_jd}
                          onChange={(e) => setGeneratedJD(prev => ({ ...prev, full_jd: e.target.value }))}
                          rows={15}
                          className="font-mono text-sm"
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="snippet">
                      <div className="relative">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(generatedJD.social_snippet)}
                        >
                          {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Textarea
                          value={generatedJD.social_snippet}
                          onChange={(e) => setGeneratedJD(prev => ({ ...prev, social_snippet: e.target.value }))}
                          rows={4}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => handleInputChange('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Number of Positions</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.number_of_positions}
                    onChange={(e) => handleInputChange('number_of_positions', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Target Closure Date</Label>
                  <Input
                    type="date"
                    value={formData.target_closure_date}
                    onChange={(e) => handleInputChange('target_closure_date', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pipeline Stages</CardTitle>
                <CardDescription>Toggle stages to enable/disable (click to skip)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {defaultStages.map((stage) => (
                    <div
                      key={stage.id}
                      className={`p-2 rounded border cursor-pointer transition-colors ${
                        formData.enabled_stages.includes(stage.id)
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-gray-50 border-gray-200 opacity-50'
                      } ${stage.required ? 'cursor-not-allowed' : ''}`}
                      onClick={() => toggleStage(stage.id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className={formData.enabled_stages.includes(stage.id) ? '' : 'line-through'}>
                          {stage.label}
                        </span>
                        {stage.required && (
                          <Badge variant="outline" className="text-xs">Required</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Create Requisition</>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
